import { HttpParams } from '@angular/common/http';
import { Component, OnInit } from '@angular/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { combineLatest, finalize, Observable, take } from 'rxjs';
import { LOCALISATIONS } from 'src/app/model/localisations';
import { AuthService } from 'src/app/modules/auth/services/auth.service';
import { ActivityService } from 'src/app/modules/agenda/services/activity-services';
import { ConfirmationInvitation, Invitation } from './invitation.model';

// ===== REFONTE : formulaire de confirmation des invités sans compte FODE =====
// Deux routes publiques mènent ici :
//  - activity/invitation/:token                    → nouveau lien du mail (jeton secret)
//  - activity/confirmation/:idActivity/:idInvite   → ancien lien (transition, à retirer avec l'ancien endpoint)

type Etat =
  | 'chargement'
  | 'formulaire'
  | 'confirme'
  | 'deja'
  | 'invalide'
  | 'erreur';

@Component({
  selector: 'app-invite-form',
  templateUrl: './invite-form.component.html',
  styleUrls: ['./invite-form.component.css'],
})
export class InviteFormComponent implements OnInit {
  etat: Etat = 'chargement';
  invitation: Invitation | null = null;
  regions: { name: string }[] = LOCALISATIONS.Regions ?? [];

  formulaire: FormGroup;
  envoi = false;
  messageErreur: string | null = null;

  private token: string | null = null;
  // Ancien lien (transition)
  private idActivity: number | null = null;
  private idInvite: string | null = null;

  constructor(
    private route: ActivatedRoute,
    private activityService: ActivityService,
    private service: AuthService,
    formBuilder: FormBuilder,
  ) {
    this.formulaire = formBuilder.group({
      structure: ['', [Validators.required, Validators.maxLength(150)]],
      fonction: ['', [Validators.required, Validators.maxLength(150)]],
      lieuProvenance: ['', Validators.required],
      numeroWave: ['', [Validators.required, Validators.pattern(/^\d{9}$/)]],
    });
  }

  ngOnInit(): void {
    const params = this.route.snapshot.paramMap;
    this.token = params.get('token');
    if (!this.token) {
      this.idActivity = Number(params.get('idActivity')) || null;
      this.idInvite = params.get('idInvite');
    }
    this.charger();
  }

  charger(): void {
    this.etat = 'chargement';
    const source$ = this.token
      ? this.activityService.getInvitation(this.token)
      : this.chargerAncienLien();
    if (!source$) {
      this.etat = 'invalide';
      return;
    }
    source$.pipe(take(1)).subscribe({
      next: (invitation) => {
        this.invitation = invitation;
        this.etat = this.etatDepuisStatut(invitation);
      },
      error: (err) =>
        (this.etat =
          err?.status === 404 || err?.status === 400 ? 'invalide' : 'erreur'),
    });
  }

  private etatDepuisStatut(invitation: Invitation): Etat {
    switch (invitation.statut) {
      case 'DEJA_CONFIRME':
        return 'deja';
      case 'CLOSE':
        return 'invalide';
      default:
        return 'formulaire';
    }
  }

  // ---------------------------------------------------------------- envoi
  confirmer(): void {
    if (this.envoi) return;
    if (this.formulaire.invalid) {
      this.formulaire.markAllAsTouched();
      return;
    }
    this.envoi = true;
    this.messageErreur = null;

    const v = this.formulaire.value;
    const demande: ConfirmationInvitation = {
      structure: v.structure.trim(),
      fonction: v.fonction.trim(),
      lieuProvenance: v.lieuProvenance,
      numeroWave: v.numeroWave,
    };

    const envoi$ = this.token
      ? this.activityService.confirmerInvitation(this.token, demande)
      : this.confirmerAncienLien(demande);

    envoi$
      .pipe(
        take(1),
        finalize(() => (this.envoi = false)),
      )
      .subscribe({
        next: (invitation) => {
          this.invitation = invitation;
          this.etat = 'confirme';
          window.scrollTo({ top: 0, behavior: 'smooth' });
        },
        error: (err) => {
          if (err?.status === 404) {
            this.etat = 'invalide';
            return;
          }
          this.messageErreur =
            err?.error?.message ??
            "Votre confirmation n'a pas pu être enregistrée. Réessayez dans un instant.";
        },
      });
  }

  // ===================================================================================
  // ===== DÉBUT TRANSITION : ancien lien (idActivity + idInvite) =====
  // À supprimer quand l'ancien endpoint /opencontroller/activity/confirmation sera coupé.
  // Mêmes appels qu'avant, ramenés au format de la nouvelle page.
  // ===================================================================================
  private chargerAncienLien(): Observable<Invitation> | null {
    if (!this.idActivity || !this.idInvite) return null;
    const idInvite = this.idInvite;

    return new Observable<Invitation>((abonne) => {
      const sub = combineLatest([
        this.service.checkConfirmation(idInvite),
        this.service.getActivityById(this.idActivity!),
        this.service.getInviteById(idInvite),
      ]).subscribe({
        next: ([dejaConfirme, activite, reponseInvite]: [any, any, any]) => {
          const invite = reponseInvite?.object;
          if (!activite || !invite) {
            abonne.error({ status: 404 });
            return;
          }
          abonne.next({
            nomActivite: activite.nomActivity,
            dateActivite: activite.openDate ?? null,
            heure: activite.hours ?? null,
            adresse: activite.adresse ?? null,
            lienLieu: activite.accessLink ?? null,
            typeActivite: activite.activityType ?? null,
            prenom: invite.firstName ?? '',
            nom: invite.lastName ?? '',
            email: invite.email ?? '',
            statut: activite.isClosed
              ? 'CLOSE'
              : dejaConfirme
                ? 'DEJA_CONFIRME'
                : 'A_CONFIRMER',
          });
          abonne.complete();
        },
        error: (err) => abonne.error(err),
      });
      return () => sub.unsubscribe();
    });
  }

  private confirmerAncienLien(
    demande: ConfirmationInvitation,
  ): Observable<Invitation> {
    const params = new HttpParams()
      .set('idInvite', this.idInvite ?? '')
      .set('activityId', String(this.idActivity ?? ''));
    const dto = {
      function: demande.fonction,
      structure: demande.structure,
      numberWave: demande.numeroWave,
      lieuProvenane: demande.lieuProvenance,
    };
    return new Observable<Invitation>((abonne) => {
      const sub = this.service.confirmParticipation({ params }, dto).subscribe({
        next: (reponse: any) => {
          // L'ancien endpoint répond toujours 200 HTTP : le vrai résultat est dans « status »
          if (reponse?.status === 200 || reponse?.status === 201) {
            abonne.next({
              ...(this.invitation as Invitation),
              statut: 'DEJA_CONFIRME',
            });
            abonne.complete();
          } else {
            abonne.error({ status: reponse?.status === 404 ? 404 : 500 });
          }
        },
        error: (err) => abonne.error(err),
      });
      return () => sub.unsubscribe();
    });
  }
  // ===== FIN TRANSITION =====

  /** Ne garde que les chiffres (collage d'un numéro avec espaces ou indicatif) */
  nettoyerNumero(): void {
    const ctrl = this.formulaire.get('numeroWave');
    let chiffres = String(ctrl?.value ?? '').replace(/\D/g, '');
    if (chiffres.length > 9 && chiffres.startsWith('221'))
      chiffres = chiffres.slice(3);
    ctrl?.setValue(chiffres.slice(0, 9), { emitEvent: false });
  }

  // ---------------------------------------------------------------- affichage
  invalide(champ: string): boolean {
    const ctrl = this.formulaire.get(champ);
    return !!ctrl && ctrl.invalid && ctrl.touched;
  }

  get initiales(): string {
    return (
      (this.invitation?.prenom?.[0] ?? '') + (this.invitation?.nom?.[0] ?? '')
    ).toUpperCase();
  }

  get estAtelierForceN(): boolean {
    return this.invitation?.typeActivite === 'ATELIER_FORCEN';
  }

  formatHeure(heure: string | null | undefined): string {
    if (!heure) return '';
    const [h, m] = heure.split(':');
    return m ? `${Number(h)} h ${m}` : heure;
  }

  majuscule(texte: string | null): string {
    return texte ? texte.charAt(0).toUpperCase() + texte.slice(1) : '';
  }
}
// ===== FIN REFONTE =====
